import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type MouseEvent,
} from "react";
import {
  CalendarCheck2,
  CalendarDays,
  BellRing,
  Check,
  ChevronRight,
  CircleAlert,
  Clock3,
  FileCheck2,
  FileWarning,
  FileUp,
  Filter,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
  Umbrella,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import { readToken, tenantTokenKey } from "../../app/session";
import { CroatianDateInput } from "../../components/CroatianDateInput";
import { useTranslation } from "../../i18n/i18n";
import { ApiError, apiRequest } from "../../lib/api";
import { useTenantApp } from "../app/TenantAppContext";

interface LeaveType {
  id: string;
  name: string;
  color: string;
  code: string;
  requiresApproval: boolean;
  deductsAllowance: boolean;
  requiresDocument: boolean;
}

interface LeaveRequest {
  id: string;
  userId: string;
  userName: string;
  leaveTypeId: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  leaveTypeColor: string;
  deductsAllowance: boolean;
  startsOn: string;
  endsOn: string;
  totalDays: number;
  reason: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  decidedByName: string | null;
  hasDocument: boolean;
  requiresDocument: boolean;
  missingDocumentation: boolean;
  documentationRemindedAt: string | null;
  documentationReminderCount: number;
  canUploadDocumentation: boolean;
  canRemindDocumentation: boolean;
  canDecide: boolean;
  canCancel: boolean;
}

interface LeaveBalance {
  userId: string;
  userName: string;
  entitledDays: number;
  carriedOverDays: number;
  adjustmentDays: number;
  totalDays: number;
  approvedDays: number;
  pendingDays: number;
  remainingDays: number;
  note: string | null;
}

interface Holiday {
  id?: string;
  date: string;
  name: string;
  source: "STATUTORY" | "COMPANY";
}

interface BalanceResponse {
  year: number;
  defaultAnnualLeaveDays: number;
  canManage: boolean;
  items: LeaveBalance[];
}

const statusOrder = [
  "ALL",
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
] as const;
const statusLabels: Record<string, string> = {
  ALL: "Svi statusi",
  PENDING: "Na čekanju",
  APPROVED: "Odobreno",
  REJECTED: "Odbijeno",
  CANCELLED: "Otkazano",
};

function cleanDate(value: string): string {
  return value.slice(0, 10);
}

export function LeavePage() {
  const { context } = useTenantApp();
  const { locale, t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [defaultDays, setDefaultDays] = useState(20);
  const [canManage, setCanManage] = useState(false);
  const [requestModal, setRequestModal] = useState(false);
  const [detail, setDetail] = useState<LeaveRequest | null>(null);
  const [managementModal, setManagementModal] = useState(false);
  const [decision, setDecision] = useState<"APPROVED" | "REJECTED" | null>(
    null,
  );
  const [decisionNote, setDecisionNote] = useState("");
  const [form, setForm] = useState({
    leaveTypeId: "",
    startsOn: "",
    endsOn: "",
    reason: "",
    medicalDocument: null as null | {
      originalFilename: string;
      fileDataUrl: string;
    },
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "ALL",
    leaveTypeId: "",
    userId: "",
    from: "",
    to: "",
    documentation: "ALL",
  });
  const [documentationFile, setDocumentationFile] = useState<null | {
    originalFilename: string;
    fileDataUrl: string;
  }>(null);
  const [balanceEditor, setBalanceEditor] = useState<LeaveBalance | null>(null);
  const [balanceForm, setBalanceForm] = useState({
    entitledDays: 20,
    carriedOverDays: 0,
    adjustmentDays: 0,
    note: "",
  });
  const [holidayForm, setHolidayForm] = useState({ date: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [notice, setNotice] = useState("");

  const token = () => readToken(tenantTokenKey);

  async function load(targetYear = year) {
    setError("");
    try {
      const [typeResponse, requestResponse, balanceResponse, holidayResponse] =
        await Promise.all([
          apiRequest<{ items: LeaveType[] }>("/api/v1/leave/types", {
            accessToken: token(),
          }),
          apiRequest<{ items: LeaveRequest[] }>("/api/v1/leave", {
            accessToken: token(),
          }),
          apiRequest<BalanceResponse>(
            `/api/v1/leave/balances?year=${targetYear}`,
            { accessToken: token() },
          ),
          apiRequest<{ items: Holiday[] }>(
            `/api/v1/leave/holidays?year=${targetYear}`,
            { accessToken: token() },
          ),
        ]);
      setTypes(typeResponse.items);
      setRequests(requestResponse.items);
      setBalances(balanceResponse.items);
      setDefaultDays(balanceResponse.defaultAnnualLeaveDays);
      setCanManage(balanceResponse.canManage);
      setHolidays(holidayResponse.items);
      setForm((current) => ({
        ...current,
        leaveTypeId: current.leaveTypeId || typeResponse.items[0]?.id || "",
      }));
      const requestedId = new URLSearchParams(window.location.search).get(
        "request",
      );
      if (requestedId) {
        const requested = requestResponse.items.find(
          (item) => item.id === requestedId,
        );
        if (requested) setDetail(requested);
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Odsustva trenutačno nisu dostupna.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(year);
  }, [year]);

  const ownBalance = balances.find((item) => item.userId === context.user.id);
  const visiblePeople = useMemo(
    () =>
      [
        ...new Map(
          requests.map((item) => [item.userId, item.userName]),
        ).entries(),
      ]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [requests],
  );
  const filtered = useMemo(() => {
    const needle = filters.search.trim().toLocaleLowerCase(locale);
    return requests.filter((item) => {
      if (filters.status !== "ALL" && item.status !== filters.status)
        return false;
      if (filters.leaveTypeId && item.leaveTypeId !== filters.leaveTypeId)
        return false;
      if (filters.userId && item.userId !== filters.userId) return false;
      if (filters.from && cleanDate(item.endsOn) < filters.from) return false;
      if (filters.to && cleanDate(item.startsOn) > filters.to) return false;
      if (filters.documentation === "MISSING" && !item.missingDocumentation)
        return false;
      if (filters.documentation === "ATTACHED" && !item.hasDocument)
        return false;
      return (
        !needle ||
        `${item.userName} ${item.leaveTypeName} ${item.reason ?? ""}`
          .toLocaleLowerCase(locale)
          .includes(needle)
      );
    });
  }, [filters, locale, requests]);

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(`${cleanDate(value)}T12:00:00`));

  function openRequest() {
    setModalError("");
    setRequestModal(true);
  }

  function openDetail(item: LeaveRequest) {
    setModalError("");
    setDecision(null);
    setDecisionNote("");
    setDocumentationFile(null);
    setDetail(item);
  }

  function stop(event: MouseEvent) {
    event.stopPropagation();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setModalError("");
    try {
      await apiRequest("/api/v1/leave", {
        method: "POST",
        accessToken: token(),
        body: JSON.stringify({
          leaveTypeId: form.leaveTypeId,
          startsOn: form.startsOn,
          endsOn: form.endsOn,
          reason: form.reason,
          ...(form.medicalDocument
            ? { medicalDocument: form.medicalDocument }
            : {}),
        }),
      });
      setRequestModal(false);
      setForm({
        leaveTypeId: types[0]?.id ?? "",
        startsOn: "",
        endsOn: "",
        reason: "",
        medicalDocument: null,
      });
      setNotice("Zahtjev je uspješno evidentiran.");
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Zahtjev nije moguće poslati.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function chooseMedicalDocument(file?: File) {
    if (!file) return;
    setModalError("");
    if (file.size > 8 * 1024 * 1024) {
      setModalError("Dokument smije imati najviše 8 MB.");
      return;
    }
    const fileDataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    setForm({
      ...form,
      medicalDocument: { originalFilename: file.name, fileDataUrl },
    });
  }

  async function chooseLaterDocumentation(file?: File) {
    if (!file) return;
    setModalError("");
    if (file.size > 8 * 1024 * 1024) {
      setModalError("Dokument smije imati najviše 8 MB.");
      return;
    }
    const fileDataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    setDocumentationFile({ originalFilename: file.name, fileDataUrl });
  }

  async function uploadDocumentation() {
    if (!detail || !documentationFile) return;
    setBusy(true);
    setModalError("");
    try {
      await apiRequest(`/api/v1/leave/${detail.id}/documentation`, {
        method: "POST",
        accessToken: token(),
        body: JSON.stringify(documentationFile),
      });
      setDocumentationFile(null);
      setDetail(null);
      setNotice(
        "Doznaka je dodana uz bolovanje i vidljiva je ovlaštenom voditelju.",
      );
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Dokumentaciju nije moguće dodati.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remindDocumentation() {
    if (!detail || !detail.canRemindDocumentation) return;
    setBusy(true);
    setModalError("");
    try {
      await apiRequest(`/api/v1/leave/${detail.id}/documentation/remind`, {
        method: "POST",
        accessToken: token(),
      });
      setDetail(null);
      setNotice(
        "Podsjetnik je stavljen u red za obavezni e-mail i PWA obavijest radniku.",
      );
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Podsjetnik nije moguće poslati.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitDecision() {
    if (!detail || !decision) return;
    if (decision === "REJECTED" && decisionNote.trim().length < 3) {
      setModalError("Upišite jasan razlog odbijanja.");
      return;
    }
    setBusy(true);
    setModalError("");
    try {
      await apiRequest(`/api/v1/leave/${detail.id}/decision`, {
        method: "PATCH",
        accessToken: token(),
        body: JSON.stringify({ decision, note: decisionNote }),
      });
      setDetail(null);
      setDecision(null);
      setNotice(
        decision === "APPROVED"
          ? "Odsustvo je odobreno."
          : "Odsustvo je odbijeno.",
      );
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Odluku nije moguće spremiti.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function cancelRequest() {
    if (!detail || !detail.canCancel || !confirm("Otkazati ovo odsustvo?"))
      return;
    setBusy(true);
    setModalError("");
    try {
      await apiRequest(`/api/v1/leave/${detail.id}/cancel`, {
        method: "PATCH",
        accessToken: token(),
      });
      setDetail(null);
      setNotice("Odsustvo je otkazano.");
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Odsustvo nije moguće otkazati.",
      );
    } finally {
      setBusy(false);
    }
  }

  function editBalance(item: LeaveBalance) {
    setBalanceEditor(item);
    setBalanceForm({
      entitledDays: item.entitledDays,
      carriedOverDays: item.carriedOverDays,
      adjustmentDays: item.adjustmentDays,
      note: item.note ?? "",
    });
    setModalError("");
  }

  async function saveBalance(event: FormEvent) {
    event.preventDefault();
    if (!balanceEditor) return;
    setBusy(true);
    setModalError("");
    try {
      await apiRequest(
        `/api/v1/leave/balances/${balanceEditor.userId}/${year}`,
        {
          method: "PUT",
          accessToken: token(),
          body: JSON.stringify(balanceForm),
        },
      );
      setBalanceEditor(null);
      setNotice(`Fond za ${balanceEditor.userName} je spremljen.`);
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Fond nije moguće spremiti.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveDefaultDays() {
    setBusy(true);
    setModalError("");
    try {
      await apiRequest("/api/v1/leave/settings", {
        method: "PATCH",
        accessToken: token(),
        body: JSON.stringify({ defaultAnnualLeaveDays: defaultDays }),
      });
      setNotice("Zadani fond godišnjeg je spremljen.");
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Postavku nije moguće spremiti.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function addHoliday(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setModalError("");
    try {
      await apiRequest("/api/v1/leave/holidays", {
        method: "POST",
        accessToken: token(),
        body: JSON.stringify(holidayForm),
      });
      setHolidayForm({ date: "", name: "" });
      setNotice("Interni neradni dan je dodan.");
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Neradni dan nije moguće dodati.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function removeHoliday(id: string) {
    if (!confirm("Obrisati interni neradni dan?")) return;
    setBusy(true);
    try {
      await apiRequest(`/api/v1/leave/holidays/${id}`, {
        method: "DELETE",
        accessToken: token(),
      });
      await load();
    } catch (caught) {
      setModalError(
        caught instanceof ApiError
          ? caught.message
          : "Neradni dan nije moguće obrisati.",
      );
    } finally {
      setBusy(false);
    }
  }

  const pendingForMe = requests.filter(
    (item) => item.canDecide && item.status === "PENDING",
  ).length;
  const missingDocuments = requests.filter(
    (item) => item.missingDocumentation,
  ).length;

  return (
    <div className="module-page leave-page">
      <header className="module-page__hero">
        <div>
          <span className="eyebrow">LJUDI · ODSUSTVA</span>
          <h1>{t("leave.title")}</h1>
          <p>
            Godišnji fond, bolovanja i odluke voditelja na jednom preglednom
            mjestu.
          </p>
        </div>
        <div className="leave-hero-actions">
          {canManage && (
            <button
              className="button button--ghost"
              onClick={() => {
                setModalError("");
                setManagementModal(true);
              }}
            >
              <Settings2 size={18} /> Fondovi i neradni dani
            </button>
          )}
          <button className="button button--primary" data-mobile-fab onClick={openRequest} aria-label="Novi zahtjev za odsustvo">
            <Plus size={18} /> {t("leave.new")}
          </button>
        </div>
      </header>

      {notice && (
        <button className="leave-notice" onClick={() => setNotice("")}>
          <Check /> {notice}
          <X />
        </button>
      )}
      {error && <div className="form-error">{error}</div>}

      <section className="leave-summary leave-summary--premium">
        <article className="leave-summary__balance">
          <Umbrella />
          <div>
            <span>
              <strong>{ownBalance?.remainingDays ?? 0}</strong>
              <em> / {ownBalance?.totalDays ?? defaultDays}</em>
            </span>
            <small>Preostalo dana godišnjeg za {year}.</small>
          </div>
          <div className="leave-balance-bar">
            <span
              style={{
                width: `${Math.max(0, Math.min(100, ownBalance?.totalDays ? (ownBalance.remainingDays / ownBalance.totalDays) * 100 : 0))}%`,
              }}
            />
          </div>
        </article>
        <article>
          <Clock3 />
          <div>
            <strong>{pendingForMe}</strong>
            <small>Čeka moju odluku</small>
          </div>
        </article>
        <article className={missingDocuments ? "leave-summary__warning" : ""}>
          <FileWarning />
          <div>
            <strong>{missingDocuments}</strong>
            <small>Nedostaje dokumentacija</small>
          </div>
        </article>
      </section>

      <section className="data-card leave-data-card">
        <header className="data-toolbar leave-toolbar">
          <label className="compact-search">
            <Search />
            <input
              value={filters.search}
              onChange={(event) =>
                setFilters({ ...filters, search: event.target.value })
              }
              placeholder="Zaposlenik, vrsta ili napomena…"
            />
          </label>
          <label className="compact-filter">
            <Filter />
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters({ ...filters, status: event.target.value })
              }
            >
              {statusOrder.map((status) => (
                <option key={status} value={status}>
                  {statusLabels[status]}
                </option>
              ))}
            </select>
          </label>
          <select
            className="compact-select"
            value={filters.leaveTypeId}
            onChange={(event) =>
              setFilters({ ...filters, leaveTypeId: event.target.value })
            }
          >
            <option value="">Sve vrste</option>
            {types.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          <select
            className="compact-select"
            value={filters.documentation}
            onChange={(event) =>
              setFilters({ ...filters, documentation: event.target.value })
            }
          >
            <option value="ALL">Sva dokumentacija</option>
            <option value="MISSING">Nedostaje doznaka</option>
            <option value="ATTACHED">Dokument priložen</option>
          </select>
          {visiblePeople.length > 1 && (
            <select
              className="compact-select"
              value={filters.userId}
              onChange={(event) =>
                setFilters({ ...filters, userId: event.target.value })
              }
            >
              <option value="">Svi zaposlenici</option>
              {visiblePeople.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          )}
          <div className="leave-date-filters">
            <CroatianDateInput ariaLabel="Od datuma" value={filters.from} onValueChange={(value) => setFilters({ ...filters, from: value })} />
            <span>–</span>
            <CroatianDateInput ariaLabel="Do datuma" value={filters.to} min={filters.from} onValueChange={(value) => setFilters({ ...filters, to: value })} />
          </div>
        </header>

        <div className="leave-table-wrap">
          <table className="leave-table">
            <thead>
              <tr>
                <th>Zaposlenik</th>
                <th>Vrsta</th>
                <th>Razdoblje</th>
                <th>Dana</th>
                <th>Dokumentacija</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Akcije</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} onClick={() => openDetail(item)}>
                  <td>
                    <span className="avatar avatar--soft">
                      {item.userName
                        .split(" ")
                        .map((part) => part[0])
                        .slice(0, 2)
                        .join("")}
                    </span>
                    <span>
                      <strong>{item.userName}</strong>
                      <small>{item.reason || "Bez dodatne napomene"}</small>
                    </span>
                  </td>
                  <td>
                    <i style={{ background: item.leaveTypeColor }} />
                    <strong>{item.leaveTypeName}</strong>
                  </td>
                  <td>
                    <strong>
                      {formatDate(item.startsOn)} – {formatDate(item.endsOn)}
                    </strong>
                  </td>
                  <td>
                    <strong>{item.totalDays}</strong>
                  </td>
                  <td>
                    {item.requiresDocument ? (
                      item.hasDocument ? (
                        <span className="documentation-state documentation-state--ok">
                          <FileCheck2 /> Priložena
                        </span>
                      ) : (
                        <span className="documentation-state documentation-state--missing">
                          <FileWarning /> Nedostaje
                        </span>
                      )
                    ) : (
                      <span className="documentation-state">Nije potrebna</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-pill status-pill--${item.status.toLowerCase()}`}
                    >
                      {statusLabels[item.status]}
                    </span>
                  </td>
                  <td>
                    <button
                      className="icon-button"
                      onClick={(event) => {
                        stop(event);
                        openDetail(item);
                      }}
                      aria-label="Otvori zahtjev"
                    >
                      <ChevronRight />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="leave-mobile-list">
          {filtered.map((item) => (
            <article
              className={`leave-mobile-card leave-mobile-card--${item.status.toLowerCase()}`}
              key={item.id}
              onClick={() => openDetail(item)}
            >
              <header>
                <div>
                  <span className="avatar avatar--soft">
                    {item.userName
                      .split(" ")
                      .map((part) => part[0])
                      .slice(0, 2)
                      .join("")}
                  </span>
                  <span>
                    <strong>{item.userName}</strong>
                    <small>{item.leaveTypeName}</small>
                  </span>
                </div>
                <span
                  className={`status-pill status-pill--${item.status.toLowerCase()}`}
                >
                  {statusLabels[item.status]}
                </span>
              </header>
              <div className="leave-mobile-card__dates">
                <CalendarDays />
                <span>
                  <strong>
                    {formatDate(item.startsOn)} – {formatDate(item.endsOn)}
                  </strong>
                  <small>{item.totalDays} radnih dana</small>
                </span>
              </div>
              {item.reason && <p>{item.reason}</p>}
              <footer>
                {item.requiresDocument ? (
                  item.hasDocument ? (
                    <span>
                      <FileCheck2 /> Dokument priložen
                    </span>
                  ) : (
                    <span className="is-missing">
                      <FileWarning /> Nedostaje doznaka
                    </span>
                  )
                ) : (
                  <span />
                )}
                {item.canDecide && <strong>Potrebna odluka</strong>}
                <ChevronRight />
              </footer>
            </article>
          ))}
        </div>

        {!loading && !filtered.length && (
          <div className="table-empty">
            <CalendarDays />
            {t("leave.empty")}
          </div>
        )}
      </section>

      {requestModal && (
        <div className="modal-backdrop">
          <form className="mini-modal leave-request-modal" onSubmit={submit}>
            <header>
              <div>
                <span className="eyebrow">NOVI ZAHTJEV</span>
                <h2>{t("leave.new")}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setRequestModal(false)}
              >
                <X />
              </button>
            </header>
            <div className="leave-request-owner">
              <span className="avatar avatar--soft">
                {context.user.firstName[0]}
                {context.user.lastName[0]}
              </span>
              <span>
                <small>Zahtjev predaje</small>
                <strong>
                  {context.user.firstName} {context.user.lastName}
                </strong>
              </span>
            </div>
            <label className="field">
              <span>Vrsta odsustva</span>
              <select
                required
                value={form.leaveTypeId}
                onChange={(event) =>
                  setForm({
                    ...form,
                    leaveTypeId: event.target.value,
                    medicalDocument: null,
                  })
                }
              >
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="form-grid">
              <label className="field">
                <span>Od datuma</span>
                <CroatianDateInput
                  required
                  value={form.startsOn}
                  onValueChange={(value) =>
                    setForm({
                      ...form,
                      startsOn: value,
                      endsOn:
                        form.endsOn && form.endsOn < value
                          ? value
                          : form.endsOn,
                    })
                  }
                  ariaLabel="Od datuma"
                />
              </label>
              <label className="field">
                <span>Do datuma</span>
                <CroatianDateInput
                  required
                  min={form.startsOn}
                  value={form.endsOn}
                  onValueChange={(value) => setForm({ ...form, endsOn: value })}
                  ariaLabel="Do datuma"
                />
              </label>
            </div>
            {types.find((type) => type.id === form.leaveTypeId)
              ?.deductsAllowance &&
              ownBalance && (
                <div className="leave-inline-balance">
                  <Umbrella />
                  <span>
                    <strong>{ownBalance.remainingDays} dana dostupno</strong>
                    <small>
                      Uključeni su odobreni i zahtjevi na čekanju za {year}.
                    </small>
                  </span>
                </div>
              )}
            <label className="field">
              <span>Razlog ili napomena</span>
              <textarea
                rows={4}
                value={form.reason}
                onChange={(event) =>
                  setForm({ ...form, reason: event.target.value })
                }
                placeholder="Kratka napomena voditelju…"
              />
            </label>
            {types.find((type) => type.id === form.leaveTypeId)
              ?.requiresDocument && (
              <label
                className={`file-drop file-drop--compact ${form.medicalDocument ? "has-file" : ""}`}
              >
                <FileUp />
                <span>
                  <strong>
                    {form.medicalDocument?.originalFilename ??
                      "Priloži doznaku ili potvrdu liječnika"}
                  </strong>
                  <small>Može i naknadno · PDF ili slika · najviše 8 MB</small>
                </span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(event) =>
                    void chooseMedicalDocument(event.target.files?.[0])
                  }
                />
              </label>
            )}
            {types.find((type) => type.id === form.leaveTypeId)?.code ===
              "BOL" && (
              <div className="info-banner">
                Bolovanje se evidentira odmah i ne čeka odobrenje. Ako doznaka
                još nije dostupna, možete je dodati naknadno; voditelj će jasno
                vidjeti da nedostaje.
              </div>
            )}
            {modalError && <div className="form-error">{modalError}</div>}
            <footer>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setRequestModal(false)}
              >
                {t("action.cancel")}
              </button>
              <button className="button button--primary" disabled={busy}>
                {busy ? "Šaljem…" : "Pošalji zahtjev"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {detail && (
        <div className="modal-backdrop">
          <div className="operational-modal leave-detail-modal">
            <header>
              <div>
                <span className="eyebrow">ZAHTJEV ZA ODSUSTVO</span>
                <h2>{detail.userName}</h2>
              </div>
              <button className="icon-button" onClick={() => setDetail(null)}>
                <X />
              </button>
            </header>
            <div className="modal-scroll">
              <section
                className={`leave-detail-status leave-detail-status--${detail.status.toLowerCase()}`}
              >
                <span className="status-pill">
                  {statusLabels[detail.status]}
                </span>
                <strong>{detail.leaveTypeName}</strong>
                <small>{detail.totalDays} radnih dana</small>
              </section>
              <section className="leave-detail-period">
                <CalendarDays />
                <div>
                  <small>Razdoblje odsustva</small>
                  <strong>
                    {formatDate(detail.startsOn)} – {formatDate(detail.endsOn)}
                  </strong>
                </div>
              </section>
              <div className="leave-detail-grid">
                <article>
                  <UserRound />
                  <span>
                    <small>Zaposlenik</small>
                    <strong>{detail.userName}</strong>
                  </span>
                </article>
                <article>
                  <Clock3 />
                  <span>
                    <small>Zahtjev predan</small>
                    <strong>{formatDate(detail.createdAt)}</strong>
                  </span>
                </article>
                <article className="leave-detail-grid__full">
                  <CircleAlert />
                  <span>
                    <small>Razlog ili napomena</small>
                    <strong>
                      {detail.reason || "Nije upisana dodatna napomena."}
                    </strong>
                  </span>
                </article>
                {detail.hasDocument && (
                  <article className="leave-detail-grid__full leave-detail-document">
                    <FileCheck2 />
                    <span>
                      <small>Dokumentacija</small>
                      <strong>
                        Doznaka ili potvrda je sigurno priložena zahtjevu.
                      </strong>
                    </span>
                  </article>
                )}
                {detail.missingDocumentation && (
                  <article className="leave-detail-grid__full leave-detail-document leave-detail-document--missing">
                    <FileWarning />
                    <span>
                      <small>Dokumentacija nije priložena</small>
                      <strong>
                        Bolovanje je otvoreno, a doznaka se očekuje naknadno.
                      </strong>
                      {detail.documentationReminderCount > 0 && (
                        <em>
                          Podsjetnik poslan {detail.documentationReminderCount}×
                          {detail.documentationRemindedAt
                            ? ` · zadnji ${formatDate(detail.documentationRemindedAt)}`
                            : ""}
                        </em>
                      )}
                    </span>
                  </article>
                )}
                {detail.decidedByName && (
                  <article className="leave-detail-grid__full">
                    <CalendarCheck2 />
                    <span>
                      <small>Odluku donio/la {detail.decidedByName}</small>
                      <strong>
                        {detail.decisionNote || "Bez dodatne napomene."}
                      </strong>
                    </span>
                  </article>
                )}
              </div>
              {detail.canUploadDocumentation && (
                <section className="leave-documentation-actions">
                  <header>
                    <span>
                      <strong>
                        {detail.hasDocument
                          ? "Dodaj još jedan dokument"
                          : "Dodaj doznaku naknadno"}
                      </strong>
                      <small>
                        Dokument ostaje vezan uz ovo bolovanje i zaposlenika.
                      </small>
                    </span>
                  </header>
                  <label
                    className={`file-drop file-drop--compact ${documentationFile ? "has-file" : ""}`}
                  >
                    <FileUp />
                    <span>
                      <strong>
                        {documentationFile?.originalFilename ??
                          "Odaberi PDF ili sliku"}
                      </strong>
                      <small>Najviše 8 MB</small>
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={(event) =>
                        void chooseLaterDocumentation(event.target.files?.[0])
                      }
                    />
                  </label>
                  {documentationFile && (
                    <button
                      className="button button--primary"
                      disabled={busy}
                      onClick={() => void uploadDocumentation()}
                    >
                      <FileCheck2 /> {busy ? "Dodajem…" : "Dodaj dokumentaciju"}
                    </button>
                  )}
                </section>
              )}
              {detail.canRemindDocumentation && (
                <section className="leave-documentation-reminder">
                  <BellRing />
                  <span>
                    <strong>Doznaka još nije dostavljena</strong>
                    <small>
                      Radnik će dobiti obavezni e-mail i PWA obavijest. Ponovni
                      podsjetnik moguć je nakon 24 sata.
                    </small>
                  </span>
                  <button
                    className="button button--ghost"
                    disabled={busy}
                    onClick={() => void remindDocumentation()}
                  >
                    Pošalji podsjetnik
                  </button>
                </section>
              )}
              {detail.canDecide && detail.status === "PENDING" && (
                <section className="leave-decision-box">
                  <header>
                    <strong>Donesite odluku</strong>
                    <small>
                      Zaposlenik će odmah dobiti obavijest i e-mail.
                    </small>
                  </header>
                  <div>
                    <button
                      className={
                        decision === "APPROVED" ? "is-selected is-approve" : ""
                      }
                      onClick={() => {
                        setDecision("APPROVED");
                        setModalError("");
                      }}
                    >
                      <Check /> Odobri
                    </button>
                    <button
                      className={
                        decision === "REJECTED" ? "is-selected is-reject" : ""
                      }
                      onClick={() => {
                        setDecision("REJECTED");
                        setModalError("");
                      }}
                    >
                      <XCircle /> Odbij
                    </button>
                  </div>
                  {decision && (
                    <label className="field">
                      <span>
                        {decision === "REJECTED"
                          ? "Razlog odbijanja *"
                          : "Napomena uz odobrenje"}
                      </span>
                      <textarea
                        autoFocus
                        rows={3}
                        required={decision === "REJECTED"}
                        value={decisionNote}
                        onChange={(event) =>
                          setDecisionNote(event.target.value)
                        }
                        placeholder={
                          decision === "REJECTED"
                            ? "Objasnite zaposleniku zašto zahtjev nije odobren…"
                            : "Neobavezna napomena…"
                        }
                      />
                    </label>
                  )}
                </section>
              )}
              {modalError && <div className="form-error">{modalError}</div>}
            </div>
            <footer>
              {detail.canCancel && (
                <button
                  className="button button--danger button--soft-danger"
                  disabled={busy}
                  onClick={() => void cancelRequest()}
                >
                  <XCircle /> Otkaži zahtjev
                </button>
              )}
              <span />
              <button
                className="button button--ghost"
                onClick={() => setDetail(null)}
              >
                Zatvori
              </button>
              {decision && (
                <button
                  className={`button ${decision === "APPROVED" ? "button--success" : "button--danger"}`}
                  disabled={
                    busy ||
                    (decision === "REJECTED" && decisionNote.trim().length < 3)
                  }
                  onClick={() => void submitDecision()}
                >
                  {busy
                    ? "Spremam…"
                    : decision === "APPROVED"
                      ? "Potvrdi odobrenje"
                      : "Potvrdi odbijanje"}
                </button>
              )}
            </footer>
          </div>
        </div>
      )}

      {managementModal && (
        <div className="modal-backdrop">
          <div className="operational-modal leave-management-modal">
            <header>
              <div>
                <span className="eyebrow">POSTAVKE ODSUSTVA</span>
                <h2>Fondovi i neradni dani</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setManagementModal(false)}
              >
                <X />
              </button>
            </header>
            <div className="modal-scroll leave-management-content">
              <section className="leave-management-heading">
                <div>
                  <strong>Godišnji fondovi</strong>
                  <small>
                    Odobreni i rezervirani dani računaju se automatski.
                  </small>
                </div>
                <label>
                  <span>Godina</span>
                  <select
                    value={year}
                    onChange={(event) => setYear(Number(event.target.value))}
                  >
                    {[currentYear - 1, currentYear, currentYear + 1].map(
                      (item) => (
                        <option value={item} key={item}>
                          {item}
                        </option>
                      ),
                    )}
                  </select>
                </label>
              </section>
              <section className="leave-default-days">
                <div>
                  <strong>Zadani godišnji fond</strong>
                  <small>
                    Primjenjuje se zaposlenicima bez pojedinačne postavke.
                  </small>
                </div>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={defaultDays}
                  onChange={(event) =>
                    setDefaultDays(Number(event.target.value))
                  }
                />
                <button
                  className="button button--small button--ghost"
                  disabled={busy}
                  onClick={() => void saveDefaultDays()}
                >
                  Spremi
                </button>
              </section>
              <div className="leave-balance-admin-list">
                {balances.map((item) => (
                  <article key={item.userId}>
                    <span className="avatar avatar--soft">
                      {item.userName
                        .split(" ")
                        .map((part) => part[0])
                        .slice(0, 2)
                        .join("")}
                    </span>
                    <div>
                      <strong>{item.userName}</strong>
                      <small>
                        Iskorišteno {item.approvedDays} · rezervirano{" "}
                        {item.pendingDays}
                      </small>
                    </div>
                    <span>
                      <strong>{item.remainingDays}</strong>
                      <small>preostalo</small>
                    </span>
                    <button
                      className="icon-button"
                      onClick={() => editBalance(item)}
                    >
                      <Pencil />
                    </button>
                  </article>
                ))}
              </div>
              <section className="leave-management-heading leave-management-heading--holidays">
                <div>
                  <strong>Neradni dani u {year}.</strong>
                  <small>
                    Državni blagdani ulaze automatski; ovdje dodajete interne
                    neradne dane.
                  </small>
                </div>
              </section>
              <form className="leave-holiday-form" onSubmit={addHoliday}>
                <label className="field">
                  <span>Datum</span>
                  <CroatianDateInput
                    required
                    value={holidayForm.date}
                    onValueChange={(value) =>
                      setHolidayForm({
                        ...holidayForm,
                        date: value,
                      })
                    }
                    ariaLabel="Datum neradnog dana"
                  />
                </label>
                <label className="field">
                  <span>Naziv</span>
                  <input
                    required
                    minLength={2}
                    value={holidayForm.name}
                    onChange={(event) =>
                      setHolidayForm({
                        ...holidayForm,
                        name: event.target.value,
                      })
                    }
                    placeholder="Kolektivni godišnji…"
                  />
                </label>
                <button
                  className="button button--primary button--small"
                  disabled={busy}
                >
                  <Plus /> Dodaj
                </button>
              </form>
              <div className="leave-holiday-list">
                {holidays.map((holiday) => (
                  <article key={`${holiday.source}-${holiday.date}`}>
                    <time>{formatDate(holiday.date)}</time>
                    <span>
                      <strong>{holiday.name}</strong>
                      <small>
                        {holiday.source === "STATUTORY"
                          ? "Državni blagdan"
                          : "Interni neradni dan"}
                      </small>
                    </span>
                    {holiday.source === "COMPANY" && holiday.id && (
                      <button
                        className="icon-button icon-button--danger"
                        onClick={() => void removeHoliday(holiday.id!)}
                      >
                        <Trash2 />
                      </button>
                    )}
                  </article>
                ))}
              </div>
              {modalError && <div className="form-error">{modalError}</div>}
            </div>
            <footer>
              <span />
              <button
                className="button button--primary"
                onClick={() => setManagementModal(false)}
              >
                Gotovo
              </button>
            </footer>
          </div>
        </div>
      )}

      {balanceEditor && (
        <div className="modal-backdrop modal-backdrop--nested">
          <form
            className="mini-modal leave-balance-modal"
            onSubmit={saveBalance}
          >
            <header>
              <div>
                <span className="eyebrow">GODIŠNJI FOND · {year}</span>
                <h2>{balanceEditor.userName}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setBalanceEditor(null)}
              >
                <X />
              </button>
            </header>
            <div className="form-grid">
              <label className="field">
                <span>Osnovni fond</span>
                <input
                  required
                  type="number"
                  min={0}
                  max={365}
                  step="0.5"
                  value={balanceForm.entitledDays}
                  onChange={(event) =>
                    setBalanceForm({
                      ...balanceForm,
                      entitledDays: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label className="field">
                <span>Preneseno iz prethodne godine</span>
                <input
                  required
                  type="number"
                  min={0}
                  max={365}
                  step="0.5"
                  value={balanceForm.carriedOverDays}
                  onChange={(event) =>
                    setBalanceForm({
                      ...balanceForm,
                      carriedOverDays: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label className="field">
                <span>Korekcija (+ / −)</span>
                <input
                  required
                  type="number"
                  min={-365}
                  max={365}
                  step="0.5"
                  value={balanceForm.adjustmentDays}
                  onChange={(event) =>
                    setBalanceForm({
                      ...balanceForm,
                      adjustmentDays: Number(event.target.value),
                    })
                  }
                />
              </label>
            </div>
            <label className="field">
              <span>Napomena uz fond</span>
              <textarea
                rows={3}
                value={balanceForm.note}
                onChange={(event) =>
                  setBalanceForm({ ...balanceForm, note: event.target.value })
                }
              />
            </label>
            <div className="leave-balance-preview">
              <span>Ukupni fond</span>
              <strong>
                {balanceForm.entitledDays +
                  balanceForm.carriedOverDays +
                  balanceForm.adjustmentDays}{" "}
                dana
              </strong>
            </div>
            {modalError && <div className="form-error">{modalError}</div>}
            <footer>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setBalanceEditor(null)}
              >
                Odustani
              </button>
              <button className="button button--primary" disabled={busy}>
                {busy ? "Spremam…" : "Spremi fond"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
